import React, { useState, useEffect } from "react";
import { TargetSelect } from "./TargetSelect";

const BrandImpersonationScanner = () => {
  const [domain, setDomain] = useState("");
  const [brandName, setBrandName] = useState("");
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("form");
  const [scans, setScans] = useState([]);
  const [activeTab, setActiveTab] = useState("domains");

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/scans/brand-impersonation", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error("Error fetching brand impersonation scans:", err);
    }
  };

  const handleScan = async () => {
    if (!domain) {
      setError("Por favor ingrese el dominio de su marca");
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/scans/brand-impersonation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          domain, 
          brand_name: brandName || null 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error en el escaneo");
      }

      const data = await response.json();
      setResults(data.results);
      setViewMode("results");
      fetchScans();
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const loadScanDetails = async (scanId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/scans/brand-impersonation/${scanId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setResults(data.results);
        setViewMode("results");
      }
    } catch (err) {
      console.error("Error loading scan details:", err);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "critical": return "text-red-600 bg-red-100";
      case "high": return "text-orange-600 bg-orange-100";
      case "medium": return "text-yellow-600 bg-yellow-100";
      case "low": return "text-green-600 bg-green-100";
      case "info": return "text-blue-600 bg-blue-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };

  const getRiskLabel = (level) => {
    const labels = {
      critical: "CRÍTICO",
      high: "ALTO", 
      medium: "MEDIO",
      low: "BAJO",
      info: "INFO"
    };
    return labels[level] || level;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("es-AR", {
        year: "numeric", month: "short", day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <span className="text-4xl">🎭</span>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Brand Impersonation</h2>
            <p className="text-gray-500">Detecta suplantación de marca y dominios falsos</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => { setViewMode("form"); setResults(null); }}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === "form" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            🔍 Nuevo Escaneo
          </button>
          <button
            onClick={() => setViewMode("history")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === "history" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            📋 Historial ({scans.length})
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 mb-6">
        <div className="flex items-start">
          <span className="text-2xl mr-3">🛡️</span>
          <div>
            <p className="text-indigo-800 font-medium">¿Qué detecta este scanner?</p>
            <ul className="text-indigo-700 text-sm mt-1 space-y-1">
              <li>• <strong>Typosquatting:</strong> Dominios con errores tipográficos (segur1a.ar, segu-ria.ar)</li>
              <li>• <strong>Homoglyphs:</strong> Caracteres visualmente similares (seguría.ar con í)</li>
              <li>• <strong>Certificados SSL:</strong> Certificados emitidos para dominios similares</li>
              <li>• <strong>Sitios activos:</strong> Dominios con sitios web que podrían ser phishing</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Form View */}
      {viewMode === "form" && !results && (
        <div className="space-y-6">
          <TargetSelect
            label="Dominio de su Marca *"
            value={domain}
            onChange={setDomain}
            types={['domain']}
            disabled={scanning}
            helpText="Solo dominios con autorización de escaneo vigente."
            testid="brand-target-select"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de la Marca (opcional)
            </label>
            <input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Mi Empresa S.A."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={handleScan}
            disabled={scanning || !domain}
            className={`w-full py-4 rounded-lg text-white font-medium text-lg transition-colors ${
              scanning || !domain
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {scanning ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Escaneando... (puede tomar 1-2 minutos)
              </span>
            ) : (
              "🔍 Iniciar Escaneo de Brand Impersonation"
            )}
          </button>
        </div>
      )}

      {/* History View */}
      {viewMode === "history" && (
        <div className="space-y-4">
          {scans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl mb-4 block">📭</span>
              <p>No hay escaneos previos</p>
              <button onClick={() => setViewMode("form")} className="mt-4 text-indigo-600 hover:underline">
                Realizar primer escaneo
              </button>
            </div>
          ) : (
            scans.map((scan) => (
              <div
                key={scan.id}
                onClick={() => loadScanDetails(scan.id)}
                className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">{scan.config?.domain}</p>
                    <p className="text-sm text-gray-500">
                      {formatDate(scan.created_at)} • {scan.config?.brand_name || "Sin nombre"}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(scan.summary?.risk_level)}`}>
                      {scan.summary?.risk_score || 0}/100
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {scan.summary?.active_threats || 0} amenazas activas
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Results View */}
      {results && (
        <div className="space-y-6">
          <button onClick={() => { setViewMode("form"); setResults(null); }} className="text-indigo-600 hover:underline flex items-center">
            ← Volver
          </button>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-800">{results.summary?.total_variations_checked || 0}</p>
              <p className="text-xs text-gray-500">Variaciones Verificadas</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-yellow-600">{results.registered_similar_domains || 0}</p>
              <p className="text-xs text-gray-500">Dominios Registrados</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">{results.active_threats || 0}</p>
              <p className="text-xs text-gray-500">Amenazas Activas</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-orange-600">{results.suspicious_certificates?.length || 0}</p>
              <p className="text-xs text-gray-500">Certificados Sospechosos</p>
            </div>
            <div className={`p-4 rounded-lg text-center ${getRiskColor(results.risk_level)}`}>
              <p className="text-2xl font-bold">{results.risk_score}/100</p>
              <p className="text-xs">{getRiskLabel(results.risk_level)}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4">
              {[
                { id: "domains", label: "Dominios Detectados", icon: "🌐" },
                { id: "certs", label: "Certificados SSL", icon: "🔒" },
                { id: "recommendations", label: "Recomendaciones", icon: "📋" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Domains Tab */}
          {activeTab === "domains" && (
            <div className="space-y-4">
              {results.typosquat_domains?.filter(d => d.is_registered).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl mb-2 block">✅</span>
                  <p>No se encontraron dominios similares registrados</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Dominios similares registrados que podrían ser usados para phishing:
                  </p>
                  <div className="space-y-2">
                    {results.typosquat_domains?.filter(d => d.is_registered).map((domain, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        domain.has_website ? "border-red-200 bg-red-50" : "border-yellow-200 bg-yellow-50"
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-mono font-medium">{domain.domain}</span>
                              {domain.has_website && <span className="text-red-500">⚠️ Sitio Activo</span>}
                              {domain.ssl_cert && <span className="text-orange-500">🔒 SSL</span>}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Técnica: {domain.technique} • IP: {domain.ip_address || "N/A"}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(domain.risk_level)}`}>
                            {getRiskLabel(domain.risk_level)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Certificates Tab */}
          {activeTab === "certs" && (
            <div className="space-y-4">
              {!results.suspicious_certificates?.length ? (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl mb-2 block">✅</span>
                  <p>No se encontraron certificados sospechosos</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Certificados SSL emitidos para dominios similares (desde Certificate Transparency logs):
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dominio</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emisor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Válido Desde</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Válido Hasta</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {results.suspicious_certificates.map((cert, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono text-sm">{cert.common_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{cert.issuer?.substring(0, 50)}...</td>
                            <td className="px-4 py-3 text-sm">{formatDate(cert.not_before)}</td>
                            <td className="px-4 py-3 text-sm">{formatDate(cert.not_after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === "recommendations" && (
            <div className="space-y-4">
              {results.recommendations?.map((rec, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border-l-4 border-indigo-500">
                  <p className="text-gray-700">{rec}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandImpersonationScanner;
